import type { SxProps, Theme } from '@mui/material';
import type { Query, QueryKey } from '@tanstack/react-query';
import { type ReactElement, type ReactNode, useState } from 'react';

import { useDebounceValue } from '@core/hooks/useDebounceValue';
import type { NestedKeyOf } from '@core/utils/types';

import { type UseSlabQueryType, useSlabQuery } from '../../hooks/useSlabQuery';
import type { ListURLParams, QueryRouteBarrelTypes } from '../../utils/ApiClient';
import type { QueryError } from '../../utils/Query';
import { InputMultiLookup } from '../InputMultiLookup/InputMultiLookup';
import type { InputMultiLookupOption } from '../InputMultiLookup/InputMultiLookupBase';
import { ErrorOption, LoadingOption } from './ApiLookupInputBase';
import {
  AlertDupeOptions,
  ConstructListQueryParams,
  type ExtractEntityTypeFromBarrel,
  type LookupInputOption,
} from './LookupInputSharedComponents';

export type RouteHelpers<QKey extends keyof QueryRouteBarrelTypes> = {
  /**
   * Constructs query parameters with type inference from the barrel.
   * The type is automatically inferred from the barrel's queryParams type.
   */
  constructQueryParams: <
    T extends ExtractEntityTypeFromBarrel<QKey> = ExtractEntityTypeFromBarrel<QKey>,
  >(
    inputText: string,
    params?: Partial<ListURLParams<T>>,
  ) => { queryParams: ListURLParams<T> };
};

type ApiMultiLookupInputProps<
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
  QOptions extends UseSlabQueryType<QKey, QBarrel>['options'] = UseSlabQueryType<
    QKey,
    QBarrel
  >['options'],
> = {
  formState: FState;
  name: FPath;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  tip?: string;
  sx?: SxProps<Theme>;
  omitEmptyHelperText?: boolean;
  route: {
    barrel: QKey;
    args: (inputText: string, helpers: RouteHelpers<QKey>) => QBarrel['args'];
    options?: QOptions;
    transform: (d: QBarrel['returns']) => LookupInputOption[];
  };
  onMatchChange?: (matches: InputMultiLookupOption<string>[]) => void;
  /** Function to match form values to options */
  formValueMatcher: (
    formValue: { id: string }[],
    options: InputMultiLookupOption<string>[],
  ) => InputMultiLookupOption<string>[];
  /** custom render function for the value of the input. */
  renderValue?: (selected: InputMultiLookupOption<string>[]) => ReactNode;
};

/**
 * An API-based multi-select lookup input component that integrates with Formik.
 *
 * @example
 * <ApiMultiLookupInput
 *   formState={formikBag.values}
 *   name='roles'
 *   label='Roles'
 *   route={{
 *     barrel: 'GET roles',
 *     args: (inputText, { constructQueryParams }) => constructQueryParams(inputText),
 *     transform: (roleList): LookupInputOption[] =>
 *       lookups({
 *         ...roleList,
 *         label: (r) => r.name,
 *       }),
 *   }}
 *   formValueMatcher={RoleValueMatcher}
 * />
 */
export const ApiMultiLookupInput = <
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
  QOptions extends UseSlabQueryType<QKey, QBarrel>['options'] = UseSlabQueryType<
    QKey,
    QBarrel
  >['options'],
>({
  route,
  onMatchChange = undefined,
  formValueMatcher,
  ...props
}: ApiMultiLookupInputProps<FState, FPath, QKey, QBarrel, QOptions>): ReactElement => {
  const [lookupText, setLookupText] = useState('');
  const debouncedLookupText = useDebounceValue(lookupText, 200);

  const helpers: RouteHelpers<QKey> = {
    constructQueryParams: <
      T extends ExtractEntityTypeFromBarrel<QKey> = ExtractEntityTypeFromBarrel<QKey>,
    >(
      inputText: string,
      params?: Partial<ListURLParams<T>>,
    ) => ConstructListQueryParams<T>(inputText, params),
  };

  const routeArgs = route.args(debouncedLookupText, helpers);

  const routeEnabled = route.options?.enabled;
  const enabled =
    typeof routeEnabled === 'function'
      ? (query: Query<QBarrel['returns'], QueryError, QBarrel['returns'], QueryKey>): boolean =>
          routeEnabled(query)
      : (routeEnabled ?? true);

  const { isLoading, isError, data } = useSlabQuery(route.barrel, routeArgs, {
    // refetchInterval must be explicit for type compilation when spreading.
    refetchInterval: route.options?.refetchInterval,
    ...route.options,
    enabled,
  });

  const options = ((): InputMultiLookupOption<string>[] => {
    if (isLoading) {
      return [LoadingOption];
    }
    if (isError) {
      return [ErrorOption];
    }
    // If there is no data, it is assumed that transform will not append options.
    if (data === undefined) {
      return [];
    }
    const opts = route.transform(data);
    AlertDupeOptions(opts);

    // Convert LookupInputOption to InputMultiLookupOption
    return opts.map((opt) => ({
      label: opt.label,
      value: opt.value,
      sublabels: opt.sublabels,
      isDisabled: opt.isDisabled,
    }));
  })();

  return (
    <InputMultiLookup
      {...props}
      options={options}
      inputValue={lookupText}
      onInputValueChange={setLookupText}
      onMatchChange={onMatchChange ?? ((): void => {})}
      formValueMatcher={formValueMatcher}
      disableClientSideFiltering
    />
  );
};
